<template>
  <div class="py-4">
    <!-- Inconsistencias -->
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
      <div class="text-h6 font-weight-bold">Qué corregir</div>
      <v-spacer />
      <v-select
        v-model="sucursal"
        :items="sucursales"
        item-text="nombre"
        item-value="uuid"
        label="Sucursal"
        clearable
        dense
        outlined
        hide-details
        style="max-width: 260px"
        @change="fetchInconsistencias"
      />
      <v-btn icon :loading="loadingInconsistencias" @click="fetchInconsistencias">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-row dense>
      <v-col v-for="t in tipos" :key="t.codigo" cols="6" sm="4" md="3">
        <v-card
          outlined
          class="pa-4 contador"
          :class="{ 'contador--activo': tipo === t.codigo, 'contador--cero': !t.cantidad }"
          @click="tipo = t.codigo"
        >
          <div class="text-h4 font-weight-black">{{ t.cantidad }}</div>
          <div class="caption grey--text">{{ t.nombre }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="seleccionado" outlined class="mt-4">
      <v-card-title class="subtitle-1 font-weight-bold">
        {{ seleccionado.nombre }}
        <span v-if="seleccionado.cantidad > seleccionado.items.length" class="caption grey--text ml-2">
          (se muestran {{ seleccionado.items.length }} de {{ seleccionado.cantidad }})
        </span>
      </v-card-title>
      <v-data-table
        :headers="headersItems"
        :items="seleccionado.items"
        item-key="id"
        dense
        hide-default-footer
        disable-pagination
        no-data-text="Nada para corregir"
      >
        <template #[`item.detalle`]="{ item }">
          <span class="caption">{{ detalle(item) }}</span>
        </template>
        <template #[`item.accion`]="{ item }">
          <v-btn small text color="primary" class="text-none" :to="linkCorregir(item)">
            Corregir
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Actividad -->
    <div class="d-flex align-center flex-wrap mt-10 mb-4" style="gap: 12px">
      <div class="text-h6 font-weight-bold">Qué se cargó</div>
      <v-spacer />
      <v-text-field v-model="desde" type="date" label="Desde" dense outlined hide-details style="max-width: 170px" />
      <v-text-field v-model="hasta" type="date" label="Hasta" dense outlined hide-details style="max-width: 170px" />
      <v-btn depressed color="primary" class="text-none" :loading="loadingActividad" @click="fetchActividad">
        Ver
      </v-btn>
    </div>

    <v-card outlined class="mb-4">
      <v-card-title class="subtitle-1 font-weight-bold">Por día y usuario</v-card-title>
      <v-data-table
        :headers="headersPorDia"
        :items="actividad.por_dia"
        dense
        :items-per-page="15"
        no-data-text="No se cargaron productos en el período"
      >
        <template v-for="campo in ['con_imagen', 'con_codigo_barras', 'con_costo']" #[`item.${campo}`]="{ item }">
          <span :key="campo" :class="item[campo] < item.cargados ? 'orange--text' : ''">
            {{ item[campo] }} / {{ item.cargados }}
          </span>
        </template>
      </v-data-table>
    </v-card>

    <v-row>
      <v-col cols="12" md="7">
        <v-card outlined>
          <v-card-title class="subtitle-1 font-weight-bold">Últimos cargados</v-card-title>
          <v-data-table
            :headers="headersUltimos"
            :items="actividad.ultimos"
            dense
            hide-default-footer
            disable-pagination
            no-data-text="Sin productos"
          >
            <template #[`item.descripcion`]="{ item }">
              <router-link :to="linkCorregir({ pe_id: item.id })">{{ item.descripcion }}</router-link>
            </template>
            <template #[`item.fecha`]="{ item }">{{ fecha(item.fecha) }}</template>
            <template v-for="campo in ['con_imagen', 'con_codigo_barras', 'con_costo']" #[`item.${campo}`]="{ item }">
              <v-icon :key="campo" small :color="item[campo] ? 'green' : 'grey'">
                {{ item[campo] ? "mdi-check" : "mdi-minus" }}
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" md="5">
        <v-card outlined>
          <v-card-title class="subtitle-1 font-weight-bold">Importaciones</v-card-title>
          <v-data-table
            :headers="headersLotes"
            :items="actividad.lotes"
            item-key="lote"
            dense
            hide-default-footer
            disable-pagination
            no-data-text="Sin importaciones en el período"
          >
            <template #[`item.desde`]="{ item }">{{ fecha(item.desde) }}</template>
            <template #[`item.origen`]="{ item }">{{ origenes[item.origen] || item.origen }}</template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
const hoy = () => new Date().toLocaleDateString("en-CA");
const haceDias = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString("en-CA");
};

/**
 * Control de carga (centro de carga F2): contadores de lo mal cargado con
 * link a la ficha para corregir, y qué se cargó por día y usuario.
 */
export default {
  name: "ProductosControlCarga",
  data() {
    return {
      inconsistencias: {},
      tipo: null,
      sucursal: null,
      sucursales: [],
      loadingInconsistencias: false,
      actividad: { por_dia: [], ultimos: [], lotes: [] },
      loadingActividad: false,
      desde: haceDias(30),
      hasta: hoy(),
      origenes: {
        IMPORTACION: "Importación",
        ALTA_MASIVA: "Alta masiva",
        LISTA_PROVEEDOR: "Lista del proveedor",
        COMPRA: "Compra",
        MANUAL: "Manual",
      },
      headersItems: [
        { text: "Producto", value: "descripcion" },
        { text: "Presentación", value: "presentacion_nombre" },
        { text: "Sucursal", value: "sucursal" },
        { text: "Detalle", value: "detalle", sortable: false },
        { text: "", value: "accion", sortable: false, align: "end" },
      ],
      headersPorDia: [
        { text: "Día", value: "fecha" },
        { text: "Usuario", value: "usuario" },
        { text: "Cargados", value: "cargados" },
        { text: "Con imagen", value: "con_imagen" },
        { text: "Con código de barras", value: "con_codigo_barras" },
        { text: "Con costo", value: "con_costo" },
      ],
      headersUltimos: [
        { text: "Producto", value: "descripcion" },
        { text: "Cuándo", value: "fecha" },
        { text: "Quién", value: "usuario" },
        { text: "Imagen", value: "con_imagen", align: "center" },
        { text: "Código", value: "con_codigo_barras", align: "center" },
        { text: "Costo", value: "con_costo", align: "center" },
      ],
      headersLotes: [
        { text: "Fecha", value: "desde" },
        { text: "Origen", value: "origen" },
        { text: "Productos", value: "productos" },
        { text: "Usuario", value: "usuario" },
      ],
    };
  },
  computed: {
    tipos() {
      return Object.entries(this.inconsistencias).map(([codigo, t]) => ({ codigo, ...t }));
    },
    seleccionado() {
      return this.tipo ? this.inconsistencias[this.tipo] : null;
    },
  },
  methods: {
    async fetchInconsistencias() {
      this.loadingInconsistencias = true;
      try {
        const params = this.sucursal ? { sucursal: this.sucursal } : {};
        const { data } = await this.axios.get("/productos/auditoria/inconsistencias", { params });
        this.inconsistencias = data;
        if (!this.tipo) {
          const primero = Object.entries(data).find(([, t]) => t.cantidad > 0);
          this.tipo = primero ? primero[0] : null;
        }
      } finally {
        this.loadingInconsistencias = false;
      }
    },
    async fetchActividad() {
      this.loadingActividad = true;
      try {
        const { data } = await this.axios.get("/productos/auditoria/actividad", {
          params: { desde: this.desde, hasta: this.hasta },
        });
        this.actividad = data;
      } finally {
        this.loadingActividad = false;
      }
    },
    async fetchSucursales() {
      try {
        const { data } = await this.axios.get("/sucursales/sucursales");
        this.sucursales = data.results || data;
      } catch (e) {
        this.sucursales = [];
      }
    },
    linkCorregir(item) {
      return { name: "empresa.producto.detalle", params: { id_producto: item.pe_id } };
    },
    detalle(item) {
      if (item.codigo_barras) return item.codigo_barras;
      if (item.venta_actual !== undefined) return `Venta $${item.venta_actual} · costo $${item.costo_actual}`;
      if (item.bases !== undefined) {
        if (item.factores_invalidos) return "Factor de conversión en 0 o negativo";
        return item.bases === 0 ? "Sin presentación base" : `${item.bases} presentaciones base`;
      }
      return "";
    },
    fecha(valor) {
      return valor ? new Date(valor).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" }) : "";
    },
  },
  created() {
    this.fetchSucursales();
    this.fetchInconsistencias();
    this.fetchActividad();
  },
};
</script>

<style scoped>
.contador {
  cursor: pointer;
  border-radius: 12px;
}
.contador--activo {
  border-color: var(--v-primary-base) !important;
  border-width: 2px;
}
.contador--cero {
  opacity: 0.55;
}
</style>
